import React from "react";
import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import type {
  SpecType,
  SpecsValidationIssue,
} from "../../../service/SpecsRevisionService";
import { createStyles, spacing } from "../../../styles/theme";
import type { SpecsImageUploadContext } from "../specsImageUpload";
import type { ObservableModelRoot } from "../specsModel/observable";
import SpecsFormEntriesGrid from "./SpecsFormEntriesGrid";
import SpecsFormEntryRenderer from "./SpecsFormEntryRenderer";
import type { SpecsFormPageSchema, SpecsFormSectionSchema } from "./types";

const styles = createStyles({
  rootBox: {
    p: spacing.groupingDesktop,
    maxWidth: "1200px",
    margin: "auto!important",
    width: "100%",
  },
  sectionPaper: {
    p: spacing.groupingDesktop,
  },
});

export default function SpecsFormPage({
  description,
  emptyMessage,
  model,
  schema,
  debugPathPrefix,
  clickToEditFields,
  imageUploadContext,
  validationIssues,
  validationSpecType,
}: {
  description: string;
  emptyMessage: string;
  model: ObservableModelRoot | null;
  schema: SpecsFormPageSchema | null;
  debugPathPrefix?: string;
  clickToEditFields?: boolean;
  imageUploadContext?: Omit<SpecsImageUploadContext, "fieldPath">;
  validationIssues?: SpecsValidationIssue[];
  validationSpecType?: SpecType;
}) {
  if (!model || !schema) {
    return (
      <Box sx={styles.rootBox}>
        <Alert severity="info">{emptyMessage}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={styles.rootBox}>
      <Stack spacing={spacing.groupingDesktop}>
        {description ? (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        ) : null}

        {schema.sections.map((section: SpecsFormSectionSchema) => (
          <Paper
            key={section.title}
            variant="outlined"
            sx={styles.sectionPaper}
          >
            <Stack spacing={2}>
              <div>
                <Typography variant="h2">{section.title}</Typography>
                {section.description ? (
                  <Typography variant="body2" color="text.secondary">
                    {section.description}
                  </Typography>
                ) : null}
              </div>
              <SpecsFormEntriesGrid
                entries={section.entries}
                basePath=""
                columns={section.columns}
                renderEntry={(entry) => (
                  <SpecsFormEntryRenderer
                    model={model}
                    entry={entry}
                    basePath=""
                    debugPathPrefix={debugPathPrefix}
                    imageUploadContext={imageUploadContext}
                    validationIssues={validationIssues}
                    validationSpecType={validationSpecType}
                    clickToEditFields={
                      clickToEditFields ?? schema.clickToEditFields
                    }
                  />
                )}
              />
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
